import { SimpleChanges } from '@angular/core';

import {
    IAdjustmentFee,
    IKeyValue,
    IReport,
    ITradeFees,
    ReportColumnNames,
    ReportExtensions
} from '@spa/api';

import {
    ReportTableData,
    ReportTableColumn,
    ReportTableDataRow,
    ReportTableGroupedColumn,
    ReportTableConfigInterface,
    ReportTableDataRowInterface
} from '../../../shared/components';

import { Helper } from 'appShared/classes';
import { ColumnSizesEnum } from 'appShared/enums';

import { EventsService } from '../services';

import { LayoutConfigInterface } from 'appEntries/panel/components';

import { BaseComponent } from './base-component';

export abstract class TradingReportWithTotalRows<ReportModel extends IReport<ITradeFees>> extends BaseComponent {
    protected feesSelectors: string[];

    public tableColumnsLength: number;
    public totalRows: ReportTableDataRow[];
    public dataTableConfig: ReportTableConfigInterface;

    protected constructor(
        public eventsService: EventsService,
        public maximumFeesFraction = 2,
        public minimumFeesFraction = 2,
        public maximumFeesFractionForTotal = 2,
        public minimumFeesFractionForTotal = 2) {

        super(eventsService);

        this.tableColumnsLength = 0;

        this.feesSelectors = [
            ReportColumnNames.COMM,
            ReportColumnNames.ECNFee,
            ReportColumnNames.SEC,
            ReportColumnNames.TAF,
            ReportColumnNames.NSCC,
            ReportColumnNames.CLR,
            ReportColumnNames.ORF,
            ReportColumnNames.PTFPF,
            ReportColumnNames.EFTT,
            ReportColumnNames.CAT,
        ];
    }

    private initTotalRows(): void {
        this.totalRows = [];

        this.getTotalRows()
            .forEach((totalReportItem, totalItemsIndex) => {
                if (totalReportItem) {
                    this.totalRows.push(this.createTotalRow(totalReportItem, totalItemsIndex));
                }
            });
    }

    private createTotalRow(model: IKeyValue<string, ITradeFees>, totalItemsIndex: number): ReportTableDataRow {
        const row: ReportTableDataRowInterface = { rowId: totalItemsIndex, dataItems: [] };

        this.dataTableConfig.columns.forEach((column: ReportTableColumn | ReportTableGroupedColumn) => {
            if (column instanceof ReportTableGroupedColumn) {
                column.childColumns.forEach((childColumn: ReportTableColumn) => {
                    row.dataItems.push(this.getTotalRowData(childColumn.selector, model));
                });
            } else {
                const colId = column.selector === ReportColumnNames.Symbol
                    ? ReportColumnNames.AssetType
                    : column.selector;

                row.dataItems.push(this.getTotalRowData(colId, model));
            }
        });

        return new ReportTableDataRow(row);
    }

    private getTotalRowData(colId: string, model: IKeyValue<string, ITradeFees>): ReportTableData {
        const value = this.feesSelectors.indexOf(colId) < 0
            ? this.mapDataTableItem(colId, model.value, true, model.key)
            : this.getFeesDataTableItemValue(colId, model.value, true);

        return new ReportTableData({
            colId,
            value,
            hidden: false,
            shifted: this.isShiftedTableData(colId),
            hasNegativeValue: !this.isShiftedTableData(colId)
                ? false
                : this.hasNegativeValue(value),
            hasIcon: this.tableDataItemWithIcon(colId),
            class: colId === ReportColumnNames.Symbol ? "symbol-cell" : null
        });
    }

    private getReportTableData(colId: string, reportItem: ITradeFees): ReportTableData {
        const isFeeSelector = this.feesSelectors.indexOf(colId) >= 0;
        const value = !isFeeSelector
            ? this.mapDataTableItem(colId, reportItem, false, null)
            : this.getFeesDataTableItemValue(colId, reportItem);

        return new ReportTableData({
            colId,
            value,
            hidden: false,
            shifted: this.isShiftedTableData(colId),
            hasNegativeValue: !this.isShiftedTableData(colId)
                ? false
                : this.hasNegativeValue(value),
            hasIcon: this.tableDataItemWithIcon(colId),
            class: colId === ReportColumnNames.Symbol ? "symbol-cell" : null
        });
    }

    protected applyAdjustmentsConfig(
        adjustments: IAdjustmentFee[],
        tableConfig: ReportTableConfigInterface) {
        let adjustmentsColumns = [];

        if (adjustments && adjustments.length > 0) {
            adjustmentsColumns = adjustments.reduce((
                childColumns: ReportTableColumn[],
                adjustment
            ) => {
                const reportColumn = childColumns
                    .find((column: ReportTableColumn) => column.selector === adjustment.category);

                if (!reportColumn) {
                    const column = new ReportTableColumn({
                        label: adjustment.category,
                        selector: adjustment.category,
                    });

                    childColumns.push(column);
                }

                return childColumns;
            }, []);
        }

        if (adjustmentsColumns.length > 0) {
            const groupedColumn = new ReportTableGroupedColumn({
                label: 'Adj Fees',
                selector: 'AdjustmentFees',
                childColumns: adjustmentsColumns,
                sortIndex: 10
            });

            tableConfig.columns.push(groupedColumn);
        }
        else {
            const singleColumn = new ReportTableColumn({
                label: 'Adj Fees',
                selector: 'AdjustmentFees',
                sortIndex: 10
            });

            tableConfig.columns.push(singleColumn);
        }
    }

    protected getFeesDataTableItemValue(
        colId: string,
        model: ITradeFees,
        isTotalRow = false): string {
        if (!model) {
            return '';
        }

        const maxFraction = isTotalRow
            ? this.maximumFeesFractionForTotal
            : this.maximumFeesFraction;
        const minFraction = isTotalRow
            ? this.minimumFeesFractionForTotal
            : this.minimumFeesFraction;

        switch (colId) {
            case ReportColumnNames.COMM:
                return ReportExtensions.toDecimalString(model.comm, maxFraction, minFraction);
            case ReportColumnNames.ECNFee:
                return ReportExtensions.toDecimalString(model.ecnFee, maxFraction, minFraction);
            case ReportColumnNames.SEC:
                return ReportExtensions.toDecimalString(model.sec, maxFraction, minFraction);
            case ReportColumnNames.TAF:
                return ReportExtensions.toDecimalString(model.taf, maxFraction, minFraction);
            case ReportColumnNames.NSCC:
                return ReportExtensions.toDecimalString(model.nscc, maxFraction, minFraction);
            case ReportColumnNames.CLR:
                return ReportExtensions.toDecimalString(model.clr, maxFraction, minFraction);
            case ReportColumnNames.ORF:
                return ReportExtensions.toDecimalString(model.orf, maxFraction, minFraction);
            case ReportColumnNames.PTFPF:
                return ReportExtensions.toDecimalString(model.ptfpf, maxFraction, minFraction);
            case ReportColumnNames.EFTT:
                return ReportExtensions.toDecimalString(model.eftt, maxFraction, minFraction);
            case ReportColumnNames.CAT:
                return ReportExtensions.toDecimalString(model.cat, maxFraction, minFraction);
            default:
                return '';
        }
    }

    abstract getReportData(): ITradeFees[];

    abstract isShiftedTableData(colId: string): boolean;

    abstract tableDataItemWithIcon(colId: string): boolean;

    abstract getReportTableConfig(): ReportTableConfigInterface;

    abstract getReportCollection(): ReportModel;

    abstract getTotalRows(): IKeyValue<string, ITradeFees>[];

    abstract mapDataTableItem(
        colId: string,
        reportItem: ITradeFees,
        isTotalRow: boolean,
        totalKey: string): string | number;

    onInit(): void {
        this.dataTableConfig = this.getReportTableConfig();

        if (this.getReportData() && this.getReportData().length > 0) {
            this.dataTableConfig.rows = this.getTableData();
        }

        this.initTotalRows();

        this.dataTableConfig.columns.forEach((column: ReportTableColumn | ReportTableGroupedColumn) => {
            if (column instanceof ReportTableGroupedColumn) {
                this.tableColumnsLength += column.childColumns.length;
            } else {
                this.tableColumnsLength++;
            }
        });
    }

    getLayoutConfig(): LayoutConfigInterface {
        return {
            mainColumnSize: ColumnSizesEnum.Col12,
            subColumnSize: null
        };
    }

    getText(): string {
        return '';
    }

    getTitle(): string {
        return '';
    }

    getTableData(): ReportTableDataRow[] {
        return this.getReportData().map((reportItem, index) => {
            const row: ReportTableDataRowInterface = { rowId: index, dataItems: [] };

            this.dataTableConfig.columns.forEach((column: ReportTableColumn | ReportTableGroupedColumn) => {
                if (column instanceof ReportTableGroupedColumn) {
                    column.childColumns.forEach((childColumn: ReportTableColumn) => {
                        row.dataItems.push(this.getReportTableData(childColumn.selector, reportItem));
                    });
                } else {
                    row.dataItems.push(this.getReportTableData(column.selector, reportItem));
                }
            });

            return new ReportTableDataRow(row);
        });
    }

    onChanges(changes: SimpleChanges): void {
        Object.keys(changes).map((key: string) => {
            if (!changes[key].firstChange) {
                this.dataTableConfig.rows = [];

                if (this.getReportData() && (this.getReportData().length > 0)) {
                    this.dataTableConfig.rows = this.getTableData();
                }

                if (this.getReportCollection()) {
                    this.initTotalRows();
                }
            }
        });
    }

    hasNegativeValue(value: string | number): boolean {
        return value && Helper.hasNegativeValue(value);
    }
}
